import { call, type Schemas } from "@saas/api-client";
import { Alert, Badge, Button, Card, ErrorAlert, formatDate, Loading, PageHeader, Table, TextField, useAsync, useCommand, usePendingId } from "@saas/ui";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api } from "./api";

const slugify = (value: string) =>
  value.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={status === "Active" ? "success" : "warning"}>{status}</Badge>;
}

export function TenantsPage() {
  const [pending, setPending] = useState(false);
  const tenants = useAsync(() => call(api.GET("/api/tenants")), [], pending ? 2000 : undefined);
  const anyPending = tenants.data?.some((t) => !t.identityProvisioned && !t.adminEmailInUse) ?? false;
  if (anyPending !== pending) setPending(anyPending);

  return (
    <>
      <PageHeader title="Tenants" description="Every organisation renting the product. Creating a tenant provisions its first TenantAdmin in the identity provider." />
      <CreateTenantForm onCreated={tenants.reload} />
      <Card title="All tenants">
        <ErrorAlert error={tenants.error} />
        {tenants.loading && !tenants.data ? <Loading /> : <TenantTable tenants={tenants.data ?? []} />}
      </Card>
    </>
  );
}

function TenantTable({ tenants }: { tenants: Schemas["TenantSummary"][] }) {
  if (tenants.length === 0) return <p className="sb-muted">No tenants yet.</p>;
  return (
    <Table caption={`${tenants.length} tenant(s)`} headers={[
        "Name",
        "Slug",
        "Status",
        "Identity provider",
        "Created",
      ]}>
      {tenants.map((t) => (
        <tr key={t.id} data-testid={`tenant-row-${t.slug}`}>
          <td>
            <Link to={`/tenants/${t.id}`}>{t.name}</Link>
          </td>
          <td>
            <code>{t.slug}</code>
          </td>
          <td>
            <StatusBadge status={t.status} />
          </td>
          <td>
            {t.identityProvisioned ? (
              <Badge tone="success">Provisioned</Badge>
            ) : t.adminEmailInUse ? (
              <Badge tone="danger">Admin email in use</Badge>
            ) : (
              <Badge tone="neutral">Pending…</Badge>
            )}
          </td>
          <td>{formatDate(t.createdAt)}</td>
        </tr>
      ))}
    </Table>
  );
}

function CreateTenantForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [created, setCreated] = useState<string>();
  const command = useCommand();
  const tenantId = usePendingId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setCreated(undefined);
    const id = tenantId.idFor(name, slug, adminEmail);
    const ok = await command.run(() => call(api.POST("/api/tenants", { body: { id, name, slug, adminEmail } })));
    if (ok) {
      tenantId.done();
      setCreated(name);
      setName("");
      setSlug("");
      setSlugTouched(false);
      setAdminEmail("");
      onCreated();
    }
  };

  return (
    <Card title="Create a tenant" description="The TenantAdmin receives an invitation email and can sign in at /tenant/.">
      <form className="sb-form" onSubmit={submit} noValidate>
        <div className="sb-form-row">
          <TextField
            label="Name"
            required
            value={name}
            error={command.fieldError("name")}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
          />
          <TextField
            label="Slug"
            required
            value={slug}
            hint="Lower-case letters, digits and dashes."
            error={command.fieldError("slug")}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
          />
          <TextField
            label="TenantAdmin email"
            type="email"
            required
            autoComplete="off"
            value={adminEmail}
            error={command.fieldError("adminEmail")}
            onChange={(e) => setAdminEmail(e.target.value)}
          />
        </div>
        {command.error && !command.fieldError("name") && !command.fieldError("slug") && !command.fieldError("adminEmail") && <ErrorAlert error={command.error} />}
        {created && <Alert tone="success">Tenant “{created}” created. Provisioning its TenantAdmin…</Alert>}
        <div>
          <Button type="submit" variant="primary" disabled={command.pending}>
            {command.pending ? "Creating…" : "Create tenant"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
